import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api/error';
import { toast } from '@/lib/toast';
import { deactivateUserRequest } from '@/services/users';

/**
 * Plan 08-02 D-82 DeactivateUserConfirmDialog — confirm-before-DELETE.
 *
 * 400 with detail "Cannot deactivate the last active Super Admin user." (T-08-07)
 * is surfaced inline and the action button is disabled to prevent re-firing the
 * mutation on a known-to-fail target.
 */

export interface DeactivateUserConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly userId: number;
  readonly userFullName: string;
  /** Called after a successful 204 — page typically navigates back to /users. */
  readonly onSuccess?: () => void;
}

export function DeactivateUserConfirmDialog({
  open,
  onOpenChange,
  userId,
  userFullName,
  onSuccess,
}: DeactivateUserConfirmDialogProps): JSX.Element {
  const [inlineError, setInlineError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const deactivateMutation = useMutation<undefined, ApiError, { id: number }>({
    mutationFn: async ({ id }) => {
      await deactivateUserRequest(id);
      return undefined;
    },
    onSuccess: () => {
      toast.success('User deactivated');
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: ['user', userId] });
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (err) => {
      setInlineError(err.detail ?? err.message);
    },
  });

  function handleClose(nextOpen: boolean): void {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setInlineError(null);
      deactivateMutation.reset();
    }
  }

  // Last-Super-Admin guard — disable Confirm if the inline error is the known guard message.
  const isGuardError = inlineError !== null && /last active super admin/i.test(inlineError);

  return (
    <AlertDialog open={open} onOpenChange={handleClose}>
      <AlertDialogContent data-testid="deactivate-user-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Deactivate {userFullName}?</AlertDialogTitle>
          <AlertDialogDescription>
            They will lose access immediately and any active sessions will be revoked. This cannot
            be undone via the UI in v1.0.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {inlineError !== null && (
          <Alert variant="destructive" data-testid="deactivate-user-error">
            <AlertTitle>Could not deactivate</AlertTitle>
            <AlertDescription>{inlineError}</AlertDescription>
          </Alert>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={deactivateMutation.isPending}
            data-testid="deactivate-user-cancel"
          >
            Cancel
          </AlertDialogCancel>
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              setInlineError(null);
              deactivateMutation.mutate({ id: userId });
            }}
            disabled={deactivateMutation.isPending || isGuardError}
            data-testid="deactivate-user-confirm"
          >
            {deactivateMutation.isPending ? 'Deactivating…' : 'Deactivate'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
